/**
 * LA BANDEJA DE CONCILIACION POR DIA (BAN-BANDEJA-PROPUESTAS-T1, Ares #28116): lo que Argemiro propone del extracto,
 * ordenado para que el administrador decida dia por dia. Sin DOM.
 *
 * Lee la respuesta de GET /api/v1/banco/propuestas (BancoPropuestasDto) tolerando camelCase y PascalCase. No calcula
 * nada del banco: los numeros son los del servidor. Lo unico que agrega es el ESTADO de cada dia para la pantalla:
 *   - la decision que traiga el servidor (decision, pedida a A en el COORD de BAN-BANDEJA) o, sin ella, la que el
 *     administrador tomo en esta misma sesion;
 *   - si no hay decision: 'cuadra' (el servidor dice Cuadra) o 'pendiente'.
 *
 * @module application/conciliacion/bandejaPropuestas
 */

const de = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const lista = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (typeof v === 'number' ? v : Number(v) || 0);

/** '2026-05' -> {desde:'2026-05-01', hasta:'2026-05-31'}. null si no tiene la forma. */
export function rangoDelMes(mes) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(mes || ''));
  if (!m) return null;
  const anio = Number(m[1]);
  const mm = Number(m[2]);
  if (mm < 1 || mm > 12) return null;
  const ultimo = new Date(Date.UTC(anio, mm, 0)).getUTCDate();
  return { desde: `${m[1]}-${m[2]}-01`, hasta: `${m[1]}-${m[2]}-${String(ultimo).padStart(2, '0')}` };
}

function movimiento(x) {
  const e = de(x, 'egresoPropuesto', 'EgresoPropuesto');
  return {
    bankMovementId: String(de(x, 'bankMovementId', 'BankMovementId') ?? ''),
    valor: num(de(x, 'valor', 'Valor')),
    descripcion: de(x, 'descripcion', 'Descripcion') ?? '',
    negocio: de(x, 'negocio', 'Negocio'),
    egresoPropuesto: e ? {
      propuestaKey: de(e, 'propuestaKey', 'PropuestaKey') ?? '',
      fecha: de(e, 'fecha', 'Fecha') ?? '',
      valor: num(de(e, 'valor', 'Valor')),
      conceptoSugerido: de(e, 'conceptoSugerido', 'ConceptoSugerido') ?? '',
      marcas: lista(de(e, 'marcas', 'Marcas')),
      decision: de(e, 'decision', 'Decision')
    } : null
  };
}

function ventaSinBanco(x) {
  return { dia: de(x, 'dia', 'Dia') ?? '', factura: de(x, 'factura', 'Factura') ?? '', valor: num(de(x, 'valor', 'Valor')) };
}

function dia(x) {
  return {
    propuestaKey: de(x, 'propuestaKey', 'PropuestaKey') ?? '',
    fecha: String(de(x, 'fecha', 'Fecha') ?? '').slice(0, 10),
    cuadra: de(x, 'cuadra', 'Cuadra') === true,
    texto: de(x, 'texto', 'Texto') ?? '',
    suman: num(de(x, 'suman', 'Suman')),
    filas: lista(de(x, 'filas', 'Filas')).map((f) => ({
      factura: de(f, 'factura', 'Factura') ?? '',
      valorCaja: num(de(f, 'valorCaja', 'ValorCaja')),
      valorBanco: num(de(f, 'valorBanco', 'ValorBanco')),
      mismoDia: de(f, 'mismoDia', 'MismoDia') === true,
      bankMovementId: String(de(f, 'bankMovementId', 'BankMovementId') ?? '')
    })),
    ventasSinBanco: lista(de(x, 'ventasSinBanco', 'VentasSinBanco')).map(ventaSinBanco),
    bancoSinVenta: lista(de(x, 'bancoSinVenta', 'BancoSinVenta')).map(movimiento),
    pagosNexo: lista(de(x, 'pagosNexo', 'PagosNexo')).map(movimiento),
    salidasSinEgreso: lista(de(x, 'salidasSinEgreso', 'SalidasSinEgreso')).map(movimiento),
    paraConciliar: num(de(x, 'paraConciliar', 'ParaConciliar')),
    // Del servidor si ya lo publica (pedido a A); si no, null y manda la decision de la sesion.
    decision: de(x, 'decision', 'Decision')
  };
}

/** La respuesta del GET, lista para pintar: dias en orden de fecha. */
export function normalizarPropuestas(data) {
  const d = data || {};
  const mes = de(d, 'mes', 'Mes') || {};
  return {
    desde: de(d, 'desde', 'Desde') ?? '',
    hasta: de(d, 'hasta', 'Hasta') ?? '',
    mes: {
      texto: de(mes, 'texto', 'Texto') ?? '',
      ventas: num(de(mes, 'ventas', 'Ventas')),
      montoVentas: num(de(mes, 'montoVentas', 'MontoVentas')),
      mismoDia: num(de(mes, 'mismoDia', 'MismoDia')),
      otroDia: num(de(mes, 'otroDia', 'OtroDia')),
      porcentaje: num(de(mes, 'porcentaje', 'Porcentaje')),
      sinBanco: num(de(mes, 'sinBanco', 'SinBanco')),
      montoSinBanco: num(de(mes, 'montoSinBanco', 'MontoSinBanco')),
      filas: lista(de(mes, 'filas', 'Filas')).map(ventaSinBanco)
    },
    dias: lista(de(d, 'dias', 'Dias')).map(dia).sort((a, b) => a.fecha.localeCompare(b.fecha))
  };
}

const DECIDIDOS = new Set(['aceptar', 'rechazar', 'revisar']);

/**
 * El estado de un dia para la pantalla. decisiones = {fecha: 'aceptar'|'rechazar'|'revisar'} de esta sesion.
 * -> 'aceptar' | 'rechazar' | 'revisar' | 'cuadra' | 'pendiente'
 */
export function estadoDelDia(d, decisiones = {}) {
  // La de la sesion primero: es posterior a lo que trajo el GET (dia en 'revisar' del servidor + aceptar ahora = aceptar).
  const deLaSesion = decisiones[d?.fecha];
  if (DECIDIDOS.has(deLaSesion)) return deLaSesion;
  const delServidor = String(d?.decision || '').toLowerCase();
  if (DECIDIDOS.has(delServidor)) return delServidor;
  return d?.cuadra ? 'cuadra' : 'pendiente';
}

/** Cuantos movimientos del banco tiene el dia a la vista (cruzados + sin venta + de Nexo + salidas sin gasto). */
export function movimientosDelDia(d) {
  return (d?.filas?.length || 0) + (d?.bancoSinVenta?.length || 0) + (d?.pagosNexo?.length || 0) + (d?.salidasSinEgreso?.length || 0);
}

// "Revisar despues" no cierra nada: el dia (o el egreso) sigue esperando un aceptar o un rechazar.
const CERRADOS = new Set(['aceptar', 'rechazar']);

/** ¿Todavia se puede decidir? Si, salvo que ya este aceptado o rechazado. */
export function admiteDecision(estado) {
  return !CERRADOS.has(String(estado || '').toLowerCase());
}

/** Cuentas para la cabecera: dias, sin decidir (cuadran + con diferencias + para revisar), decididos. */
export function resumenBandeja(dias, decisiones = {}) {
  const r = { dias: 0, sinDecidir: 0, cuadran: 0, conDiferencias: 0, paraRevisar: 0, decididos: 0, salidasSinEgreso: 0, pagosNexo: 0 };
  for (const d of lista(dias)) {
    r.dias++;
    const e = estadoDelDia(d, decisiones);
    if (CERRADOS.has(e)) r.decididos++;
    else {
      r.sinDecidir++;
      if (e === 'cuadra') r.cuadran++;
      else if (e === 'revisar') r.paraRevisar++;
      else r.conDiferencias++;
    }
    r.salidasSinEgreso += d.salidasSinEgreso.length;
    r.pagosNexo += d.pagosNexo.length;
  }
  return r;
}

/** filtro: 'todos' | 'sin-decidir' | 'diferencias' | 'decididos' */
export function filtrarDias(dias, filtro, decisiones = {}) {
  const todos = lista(dias);
  if (filtro === 'sin-decidir') return todos.filter((d) => !CERRADOS.has(estadoDelDia(d, decisiones)));
  if (filtro === 'diferencias') return todos.filter((d) => estadoDelDia(d, decisiones) === 'pendiente');
  if (filtro === 'decididos') return todos.filter((d) => CERRADOS.has(estadoDelDia(d, decisiones)));
  return todos;
}
